import { db } from '@/services/database'

export const CUSTOM_AUDIO_MAX_BYTES = 20 * 1024 * 1024
export const CUSTOM_AUDIO_MAX_FILE_BYTES = 2 * 1024 * 1024
export const CUSTOM_AUDIO_MAX_SECONDS = 10
export const CUSTOM_AUDIO_ACCEPT = '.mp3,.wav,.m4a,.ogg,.webm'
const STORAGE_RESERVE = 10 * 1024 * 1024

export interface CustomAudioClip {
  id: string
  name: string
  mime: string
  size: number
  duration: number
  blob: Blob
  createdAt: number
}
export type CustomAudioInfo = Omit<CustomAudioClip, 'blob'>

export function customAudioReason(error: unknown): string {
  const name = error && typeof error === 'object' && 'name' in error ? error.name : ''
  if (name === 'QuotaExceededError') return 'custom-audio-quota'
  if (name === 'AbortError') return 'custom-audio-cancelled'
  return error instanceof Error && error.message.startsWith('custom-audio-') ? error.message : 'custom-audio-storage'
}

export async function validateCustomAudio(file: File, signal?: AbortSignal): Promise<number> {
  signal?.throwIfAborted()
  if (!file.size) throw new Error('custom-audio-empty')
  if (file.size > CUSTOM_AUDIO_MAX_FILE_BYTES) throw new Error('custom-audio-size')
  if (!/\.(mp3|wav|m4a|ogg|webm)$/i.test(file.name)) throw new Error('custom-audio-format')
  if (typeof Audio !== 'function' || typeof URL.createObjectURL !== 'function') throw new Error('custom-audio-unavailable')
  return new Promise((resolve, reject) => {
    const audio = new Audio(), url = URL.createObjectURL(file)
    const finish = (error?: string) => {
      const duration = audio.duration
      clearTimeout(timeout)
      signal?.removeEventListener('abort', abort)
      audio.onloadedmetadata = null; audio.oncanplay = null; audio.onerror = null
      audio.pause(); audio.removeAttribute('src'); audio.load(); URL.revokeObjectURL(url)
      if (error) reject(new Error(error)); else resolve(duration)
    }
    const abort = () => finish('custom-audio-cancelled')
    const durationError = () => !Number.isFinite(audio.duration) || audio.duration <= 0 ? 'custom-audio-invalid' : audio.duration > CUSTOM_AUDIO_MAX_SECONDS ? 'custom-audio-duration' : ''
    const timeout = setTimeout(() => finish('custom-audio-timeout'), 8000)
    signal?.addEventListener('abort', abort, { once: true })
    audio.onloadedmetadata = () => { const error = durationError(); if (error) finish(error) }
    audio.oncanplay = () => finish(durationError() || undefined)
    audio.onerror = () => finish('custom-audio-invalid')
    try { audio.preload = 'auto'; audio.src = url; audio.load() }
    catch { finish('custom-audio-invalid') }
  })
}

export async function importCustomAudio(file: File, buffId: string, signal?: AbortSignal): Promise<CustomAudioClip> {
  try {
    const duration = await validateCustomAudio(file, signal)
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
    const id = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
    // Storage estimates include the site's other databases and resource caches.
    let available: number | undefined
    try {
      const estimate = await navigator.storage?.estimate()
      if (estimate?.quota !== undefined && estimate.usage !== undefined) available = estimate.quota - estimate.usage
    } catch { /* The actual write remains the authority when estimates are unavailable. */ }
    signal?.throwIfAborted()
    return await db.transaction('rw', db.customAudioClips, db.buffs, async () => {
      signal?.throwIfAborted()
      const target = await db.buffs.get(buffId)
      if (!target) throw new Error('custom-audio-target-missing')
      let clip = await db.customAudioClips.get(id)
      if (!clip) {
        const clips = await db.customAudioClips.toArray()
        if (clips.reduce((sum, entry) => sum + entry.size, 0) + file.size > CUSTOM_AUDIO_MAX_BYTES) throw new Error('custom-audio-limit')
        if (available !== undefined && available < file.size + STORAGE_RESERVE) throw new Error('custom-audio-quota')
        clip = { id, name: file.name.slice(0, 160), mime: file.type, size: file.size, duration, blob: file, createdAt: Date.now() }
        await db.customAudioClips.add(clip)
      }
      signal?.throwIfAborted()
      await db.buffs.put({ ...target, customAudioId: id, customAudioEnabled: true, updatedAt: Date.now() })
      return clip
    })
  } catch (error) { throw new Error(customAudioReason(error)) }
}

export async function bindCustomAudio(buffId: string, audioId?: string): Promise<void> {
  try {
    await db.transaction('rw', db.customAudioClips, db.buffs, async () => {
      const target = await db.buffs.get(buffId)
      if (!target) throw new Error('custom-audio-target-missing')
      if (audioId && !await db.customAudioClips.get(audioId)) throw new Error('custom-audio-missing')
      await db.buffs.put({ ...target, customAudioId: audioId || undefined, customAudioEnabled: audioId ? true : undefined, updatedAt: Date.now() })
    })
  } catch (error) { throw new Error(customAudioReason(error)) }
}

export async function deleteCustomAudio(audioId: string): Promise<void> {
  try {
    await db.transaction('rw', db.customAudioClips, db.buffs, async () => {
      await db.buffs.filter(item => item.customAudioId === audioId).modify(item => { delete item.customAudioId; delete item.customAudioEnabled; item.updatedAt = Date.now() })
      await db.customAudioClips.delete(audioId)
    })
  } catch (error) { throw new Error(customAudioReason(error)) }
}
